/**
 * The `/login/callback` landing page (DEV-5751).
 *
 * The redirect itself is finished by the shared `@cyngn/auth-react` provider above the router —
 * PKCE state check, code exchange, cloud-core provisioning and token commit all happen there —
 * so this page only reports what the provider ended up with: it lands the user, or it runs the
 * stale-session recovery that Cognito's cross-domain cookies still require.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  POST_LOGIN_RETURN_TO_KEY,
  useAuth,
} from "Components/Authentication/AuthProvider";
import { addTelemetryBreadcrumb, sendTelemetryEvent } from "Utils/faroUtils";
import {
  MAX_COGNITO_RETRIES,
  getCognitoRetryCount,
  incrementCognitoRetry,
  resetCognitoRetry,
} from "Utils/cognito";

import {
  CallbackContainerStyled,
  CallbackTitleStyled,
  CallbackMessageStyled,
  CallbackButtonStyled,
} from "./styles";

/** Landing on the login page again would bounce the user straight back into SSO. */
const landingPath = (returnTo: string | null): string =>
  !returnTo || returnTo.startsWith("/login") ? "/" : returnTo;

export const CognitoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, error: authError, logout } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cognito stores its session in cookies on its own domain
    // (e.g. .auth-dev.cyngn.com), which we can't clear from JS on this origin.
    // When the oauth2/token exchange or backend validation throws — the
    // typical stale-cookie failure mode — bounce through Cognito's /logout
    // endpoint to clear those cookies server-side, then auto-restart SSO via
    // /login. We deliberately do NOT apply this to ?error= redirects: those
    // represent Cognito's own decisions (access_denied = user cancel,
    // server_error, invalid_request) and should surface, not silently retry.
    const recoverFromStaleSession = (displayMessage: string, err?: unknown) => {
      if (getCognitoRetryCount() < MAX_COGNITO_RETRIES) {
        const attempt = getCognitoRetryCount() + 1;
        console.warn(
          "[Cognito] SSO callback failed; clearing stale session via /logout (attempt %d/%d)",
          attempt,
          MAX_COGNITO_RETRIES
        );
        addTelemetryBreadcrumb("Cognito stale-session recovery triggered", {
          category: "auth",
          attempt: String(attempt),
          max: String(MAX_COGNITO_RETRIES),
        });
        incrementCognitoRetry();
        // `logout()` clears the tokens and redirects through Cognito's logout endpoint.
        void logout();
        return;
      }
      console.warn(
        "[Cognito] SSO recovery exhausted after %d attempt(s); surfacing error to user",
        MAX_COGNITO_RETRIES
      );
      if (err) {
        const reportedError =
          err instanceof Error ? err : new Error(String(err));
        sendTelemetryEvent(reportedError, "Cognito.RecoveryExhausted");
      }
      resetCognitoRetry();
      setError(displayMessage);
    };

    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    if (errorParam) {
      console.warn(
        "[Cognito] SSO callback error:",
        errorParam,
        errorDescription
      );
      resetCognitoRetry();
      setError(errorDescription || errorParam);
      return;
    }

    if (!searchParams.get("code")) {
      // A bookmark or a refresh of /login/callback: there is nothing for the provider to
      // finish, so say so instead of bouncing through /logout on a session that never began.
      resetCognitoRetry();
      setError("No authorization code received");
      return;
    }

    if (isLoading) {
      return;
    }

    if (isAuthenticated) {
      console.info("[Cognito] SSO login complete, redirecting to app");
      const returnTo = window.sessionStorage.getItem(POST_LOGIN_RETURN_TO_KEY);
      window.sessionStorage.removeItem(POST_LOGIN_RETURN_TO_KEY);
      navigate(landingPath(returnTo), { replace: true });
      return;
    }

    console.error("[Cognito] SSO login failed:", authError);
    recoverFromStaleSession(
      "Authentication failed. Please try again.",
      authError ?? new Error("Callback produced no session")
    );
  }, [searchParams, navigate, isLoading, isAuthenticated, authError, logout]);

  if (error) {
    return (
      <CallbackContainerStyled>
        <CallbackTitleStyled>Login Failed</CallbackTitleStyled>
        <CallbackMessageStyled>{error}</CallbackMessageStyled>
        <CallbackButtonStyled onClick={() => navigate("/login")}>
          Back to Login
        </CallbackButtonStyled>
      </CallbackContainerStyled>
    );
  }

  return (
    <CallbackContainerStyled>
      <CallbackMessageStyled>Completing sign in...</CallbackMessageStyled>
    </CallbackContainerStyled>
  );
};
